import {
  UPDATE_COURSE_REGISTERED_FIELDS,
  UPDATE_REGISTRATION_FIELDS,
  UPDATE_REGISTRATION_SCORES,
  ADMIN_SUBMIT_COURSE_REGISTRATION,
  SENATE_APPROVE_STUDENT_REGISTRATION,
  UPDATE_FACULTY,
  DELETE_FACULTY,
  UPDATE_DEPARTMENT,
  DELETE_DEPARTMENT,
  UPDATE_PROGRAMME,
  DELETE_PROGRAMME,
} from '@/graphql/mutations/tertiary';

import {
  useCreateProgrammeMutation,
  useCreateFacultyMutation,
  useCreateDepartmentMutation,
  useCreateCourseMutation,
  useCreateCurriculumMutation,
  useDeleteCurriculumMutation,
  useUpdateCurriculumMutation,
  useDeleteCourseMutation,
  useUpdateCourseMutation,
  useAddCourseToRegMutation,
  useDropCourseFromRegMutation,
  useCreateProgrammeModuleMutation,
  useUpdateProgrammeModuleMutation,
  useDeleteProgrammeModuleMutation,
  useToggleProgrammeModuleMutation,
  ListSchoolFacultiesDocument,
  ListSchoolProgrammesDocument,
  ListSchoolDepartmentsDocument,
} from '@/types/vue-apollo-helpers';

import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { extractGraphQLError } from '@/utils/graphqlError';
import type {
  AddCourseToRegInput,
  AdminCourseRegistrationInput,
  CourseCreateInput,
  CourseScoreInput,
  CourseUpdateInput,
  CreateCurriculumInput,
  CreateProgrammeModuleInput,
  UpdateProgrammeModuleInput,
  DepartmentInput,
  DepartmentUpdateInput,
  DropCourseFromRegInput,
  FacultyInput,
  ProgrammeInput,
  ProgrammeUpdateInput,
  UpdateCourseRegisteredFieldsInput,
  UpdateCourseRegistrationFieldsInput,
  UpdateCurriculumInput,
} from '@/types/composition-functions';

export function useTertiaryMutations() {
  const toast = useToast();
  const confirm = useConfirm();

  const facultyRefetchQueries = [{ query: ListSchoolFacultiesDocument }];
  const departmentRefetchQueries = [{ query: ListSchoolDepartmentsDocument }];
  const programmeRefetchQueries = [{ query: ListSchoolProgrammesDocument }];

  const createFacultyMutation = useCreateFacultyMutation({ refetchQueries: facultyRefetchQueries });
  const createDepartmentMutation = useCreateDepartmentMutation({ refetchQueries: departmentRefetchQueries });
  const createProgrammeMutation = useCreateProgrammeMutation({ refetchQueries: programmeRefetchQueries });
  const createCourseMutation = useCreateCourseMutation({});
  const createCurriculumMutation = useCreateCurriculumMutation({});
  const deleteCurriculumMutation = useDeleteCurriculumMutation({});
  const updateCurriculumMutation = useUpdateCurriculumMutation({});
  const deleteCourseMutation = useDeleteCourseMutation({});
  const updateCourseMutation = useUpdateCourseMutation({});
  const addCourseToRegInput = useAddCourseToRegMutation({});
  const dropCourseFromRegInput = useDropCourseFromRegMutation({});
  const updateCourseRegisteredFieldsMutation = useMutation(UPDATE_COURSE_REGISTERED_FIELDS);
  const updateRegistrationFieldsMutation = useMutation(UPDATE_REGISTRATION_FIELDS);
  const updateRegistrationScoresMutation = useMutation(UPDATE_REGISTRATION_SCORES);
  const adminSubmitCourseRegMutation = useMutation(ADMIN_SUBMIT_COURSE_REGISTRATION);
  const senateApproveMutation = useMutation(SENATE_APPROVE_STUDENT_REGISTRATION);
  const updateFacultyMutation = useMutation(UPDATE_FACULTY, { refetchQueries: facultyRefetchQueries });
  const deleteFacultyMutation = useMutation(DELETE_FACULTY, { refetchQueries: facultyRefetchQueries });
  const updateDepartmentMutation = useMutation(UPDATE_DEPARTMENT, { refetchQueries: departmentRefetchQueries });
  const deleteDepartmentMutation = useMutation(DELETE_DEPARTMENT, { refetchQueries: departmentRefetchQueries });
  const updateProgrammeMutation = useMutation(UPDATE_PROGRAMME, { refetchQueries: programmeRefetchQueries });
  const deleteProgrammeMutation = useMutation(DELETE_PROGRAMME, { refetchQueries: programmeRefetchQueries });
  // Refetch by operation name — the active ListProgrammeModules queries carry
  // their own variables (programme/session), which a document ref would not.
  const moduleRefetchQueries = ['ListProgrammeModules'];
  const createProgrammeModuleMutation = useCreateProgrammeModuleMutation({ refetchQueries: moduleRefetchQueries });
  const updateProgrammeModuleMutation = useUpdateProgrammeModuleMutation({ refetchQueries: moduleRefetchQueries });
  const deleteProgrammeModuleMutation = useDeleteProgrammeModuleMutation({ refetchQueries: moduleRefetchQueries });
  // No refetch needed: the toggle payload returns is_active/is_open and the
  // cache updates the module row in place.
  const toggleProgrammeModuleMutation = useToggleProgrammeModuleMutation({});

  async function createTertiaryFaculty(input: FacultyInput) {
    try {
      const { data } = (await createFacultyMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Faculty created successfully!', life: 3000 });
      return data?.createFaculty;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating faculty'), life: 3000 });
      throw error;
    }
  }

  async function createFacultyDepartment(input: DepartmentInput) {
    try {
      const { data } = (await createDepartmentMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Department created successfully!', life: 3000 });
      return data?.createDepartment;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating department'), life: 3000 });
      throw error;
    }
  }

  async function createTertiaryProgramme(input: ProgrammeInput) {
    try {
      const { data } = (await createProgrammeMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Programme created successfully!', life: 3000 });
      return data?.createProgramme;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating programme'), life: 3000 });
      throw error;
    }
  }

  async function createTertiaryCourse(input: CourseCreateInput) {
    try {
      const { data } = (await createCourseMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Course created successfully!', life: 3000 });
      return data?.createCourse;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating course'), life: 3000 });
      throw error;
    }
  }

  async function createTertiaryCurriculum(input: CreateCurriculumInput) {
    try {
      const { data } = (await createCurriculumMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Curriculum created successfully!', life: 3000 });
      return data?.createCurriculum;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating curriculum'), life: 3000 });
      throw error;
    }
  }

  async function updateTertiaryCurriculum(id: string, input: UpdateCurriculumInput) {
    try {
      const { data } = (await updateCurriculumMutation.mutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Curriculum updated successfully!', life: 3000 });
      return data?.updateCurriculum;
    } catch (error) {
      const msg = extractGraphQLError(error, '');
      if (msg.includes('acknowledgeImpact')) {
        return new Promise((resolve, reject) => {
          confirm.require({
            message: msg.replace(' If you understand the implications, retry with acknowledgeImpact: true.', ''),
            header: 'Update Warning',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, update anyway',
            rejectLabel: 'Cancel',
            acceptClass: 'p-button-danger',
            group: 'impact-warning',
            accept: async() => {
              try {
                const { data } = (await updateCurriculumMutation.mutate({ id, input: { ...input, acknowledgeImpact: true } }))!;
                toast.add({ severity: 'success', summary: 'Updated', detail: 'Curriculum updated successfully!', life: 3000 });
                resolve(data?.updateCurriculum);
              } catch (retryError) {
                toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(retryError, 'Error updating curriculum'), life: 3000 });
                reject(retryError);
              }
            },
            reject: () => { resolve(null); },
          });
        });
      }
      toast.add({ severity: 'error', summary: 'Error', detail: msg || 'Error updating curriculum', life: 3000 });
      throw error;
    }
  }

  async function deleteTertiaryCurriculum(id: string) {
    try {
      const { data } = (await deleteCurriculumMutation.mutate({ id }))!;
      const result = data?.deleteCurriculum;
      if (result?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: result.message || 'Curriculum deleted', life: 3000 });
        return true;
      }
      toast.add({ severity: 'error', summary: 'Error', detail: result?.message || 'Failed to delete curriculum', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error deleting curriculum'), life: 3000 });
      throw error;
    }
  }

  async function updateTertiaryCourse(id: string, input: CourseUpdateInput) {
    try {
      const { data } = (await updateCourseMutation.mutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Course updated successfully!', life: 3000 });
      return data?.updateCourse;
    } catch (error) {
      const msg = extractGraphQLError(error, '');
      if (msg.includes('acknowledgeImpact')) {
        return new Promise((resolve, reject) => {
          confirm.require({
            message: msg.replace(' If you understand the implications, retry with acknowledgeImpact: true.', ''),
            header: 'Update Warning',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, update anyway',
            rejectLabel: 'Cancel',
            acceptClass: 'p-button-danger',
            group: 'impact-warning',
            accept: async() => {
              try {
                const { data } = (await updateCourseMutation.mutate({ id, input: { ...input, acknowledgeImpact: true } }))!;
                toast.add({ severity: 'success', summary: 'Updated', detail: 'Course updated successfully!', life: 3000 });
                resolve(data?.updateCourse);
              } catch (retryError) {
                toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(retryError, 'Error updating course'), life: 3000 });
                reject(retryError);
              }
            },
            reject: () => { resolve(null); },
          });
        });
      }
      toast.add({ severity: 'error', summary: 'Error', detail: msg || 'Error updating course', life: 3000 });
      throw error;
    }
  }

  async function deleteTertiaryCourse(id: string) {
    try {
      const { data } = (await deleteCourseMutation.mutate({ id }))!;
      const result = data?.deleteCourse;
      if (result?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: result.message || 'Course deleted', life: 3000 });
        return true;
      }
      toast.add({ severity: 'error', summary: 'Error', detail: result?.message || 'Failed to delete course', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error deleting course'), life: 3000 });
      throw error;
    }
  }

  async function addCourseToRegistration(input: AddCourseToRegInput) {
    try {
      const { data } = (await addCourseToRegInput.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Course added successfully!', life: 3000 });
      return data?.addCourseMutation;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error adding course'), life: 3000 });
      throw error;
    }
  }

  async function dropCourseFromRegistration(input: DropCourseFromRegInput) {
    try {
      const { data } = (await dropCourseFromRegInput.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Course dropped successfully!', life: 3000 });
      return data?.dropCourseMutation;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error dropping course'), life: 3000 });
      throw error;
    }
  }

  async function updateCourseRegisteredFields(input: UpdateCourseRegisteredFieldsInput) {
    try {
      const { data } = (await updateCourseRegisteredFieldsMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Course updated successfully!', life: 3000 });
      return data?.updateCourseRegisteredFields;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating course'), life: 3000 });
      throw error;
    }
  }

  async function updateRegistrationFields(input: UpdateCourseRegistrationFieldsInput) {
    try {
      const { data } = (await updateRegistrationFieldsMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Registration updated successfully!', life: 3000 });
      return data?.updateRegistrationFields;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating registration'), life: 3000 });
      throw error;
    }
  }

  async function updateRegistrationScores(input: { courseScores: CourseScoreInput[] }) {
    try {
      const { data } = (await updateRegistrationScoresMutation.mutate(input))!;
      const result = data?.updateRegistrationScores;
      if (result?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: result.message || 'Scores updated successfully!', life: 3000 });
      }
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating scores'), life: 3000 });
      throw error;
    }
  }

  async function adminSubmitCourseRegistration(input: AdminCourseRegistrationInput) {
    try {
      const { data } = (await adminSubmitCourseRegMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Course registration submitted successfully!', life: 3000 });
      return data?.adminSubmitCourseRegistration;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error submitting course registration'), life: 3000 });
      throw error;
    }
  }

  async function senateApproveRegistration(input: { student_id: string | number; academic_calendar_id: string | number }) {
    try {
      const { data } = (await senateApproveMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Results Released', detail: 'Senate approval granted. Results can now be published for this student.', life: 3000 });
      return data?.senateApproveStudentRegistration;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error granting senate approval'), life: 3000 });
      throw error;
    }
  }

  async function createProgrammeModule(input: CreateProgrammeModuleInput) {
    try {
      const { data } = (await createProgrammeModuleMutation.mutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Module created successfully!', life: 3000 });
      return data?.createProgrammeModule;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error creating module'), life: 3000 });
      throw error;
    }
  }

  async function updateProgrammeModule(id: string, input: UpdateProgrammeModuleInput) {
    try {
      const { data } = (await updateProgrammeModuleMutation.mutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Module updated successfully!', life: 3000 });
      return data?.updateProgrammeModule;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating module'), life: 3000 });
      throw error;
    }
  }

  async function toggleProgrammeModule(id: string, is_active: boolean) {
    try {
      const { data } = (await toggleProgrammeModuleMutation.mutate({ id, is_active }))!;
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: is_active ? 'Module activated.' : 'Module deactivated.',
        life: 3000,
      });
      return data?.toggleProgrammeModule;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error toggling module'), life: 3000 });
      throw error;
    }
  }

  async function deleteProgrammeModule(id: string) {
    try {
      const { data } = (await deleteProgrammeModuleMutation.mutate({ id }))!;
      const result = data?.deleteProgrammeModule;
      if (result?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: result.message || 'Module deleted', life: 3000 });
        return true;
      }
      toast.add({ severity: 'error', summary: 'Error', detail: result?.message || 'Failed to delete module', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error deleting module'), life: 3000 });
      throw error;
    }
  }

  async function updateTertiaryFaculty(id: string, input: FacultyInput) {
    try {
      const { data } = (await updateFacultyMutation.mutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Faculty updated successfully!', life: 3000 });
      return data?.updateFaculty;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating faculty'), life: 3000 });
      throw error;
    }
  }

  async function deleteTertiaryFaculty(id: string) {
    try {
      const { data } = (await deleteFacultyMutation.mutate({ id }))!;
      const result = data?.deleteFaculty;
      if (result?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: result.message || 'Faculty deleted', life: 3000 });
        return true;
      }
      toast.add({ severity: 'error', summary: 'Error', detail: result?.message || 'Failed to delete faculty', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error deleting faculty'), life: 3000 });
      throw error;
    }
  }

  async function updateTertiaryDepartment(id: string, input: DepartmentUpdateInput) {
    try {
      const { data } = (await updateDepartmentMutation.mutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Department updated successfully!', life: 3000 });
      return data?.updateDepartment;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating department'), life: 3000 });
      throw error;
    }
  }

  async function deleteTertiaryDepartment(id: string) {
    try {
      const { data } = (await deleteDepartmentMutation.mutate({ id }))!;
      const result = data?.deleteDepartment;
      if (result?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: result.message || 'Department deleted', life: 3000 });
        return true;
      }
      toast.add({ severity: 'error', summary: 'Error', detail: result?.message || 'Failed to delete department', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error deleting department'), life: 3000 });
      throw error;
    }
  }

  async function updateTertiaryProgramme(id: string, input: ProgrammeUpdateInput) {
    try {
      const { data } = (await updateProgrammeMutation.mutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'Programme updated successfully!', life: 3000 });
      return data?.updateProgramme;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating programme'), life: 3000 });
      throw error;
    }
  }

  async function deleteTertiaryProgramme(id: string) {
    try {
      const { data } = (await deleteProgrammeMutation.mutate({ id }))!;
      const result = data?.deleteProgramme;
      if (result?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: result.message || 'Programme deleted', life: 3000 });
        return true;
      }
      toast.add({ severity: 'error', summary: 'Error', detail: result?.message || 'Failed to delete programme', life: 3000 });
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error deleting programme'), life: 3000 });
      throw error;
    }
  }

  return {
    createTertiaryCourse,
    createTertiaryFaculty,
    updateTertiaryFaculty,
    deleteTertiaryFaculty,
    createTertiaryProgramme,
    updateTertiaryProgramme,
    deleteTertiaryProgramme,
    createFacultyDepartment,
    updateTertiaryDepartment,
    deleteTertiaryDepartment,
    createTertiaryCurriculum,
    updateTertiaryCurriculum,
    deleteTertiaryCurriculum,
    createProgrammeModule,
    updateProgrammeModule,
    deleteProgrammeModule,
    toggleProgrammeModule,
    updateTertiaryCourse,
    deleteTertiaryCourse,
    addCourseToRegistration,
    dropCourseFromRegistration,
    updateCourseRegisteredFields,
    updateRegistrationFields,
    updateRegistrationScores,
    adminSubmitCourseRegistration,
    senateApproveRegistration,

    creatingFaculty: createFacultyMutation.loading,
    creatingDepartment: createDepartmentMutation.loading,
    creatingProgramme: createProgrammeMutation.loading,
    creatingCourse: createCourseMutation.loading,
    creatingCurriculum: createCurriculumMutation.loading,
    updatingCurriculum: updateCurriculumMutation.loading,
    deletingCurriculum: deleteCurriculumMutation.loading,
    createdCourseLoading: createCourseMutation.loading,
    updatingCourse: updateCourseMutation.loading,
    deletingCourse: deleteCourseMutation.loading,
    addingCourseToRegLoading: addCourseToRegInput.loading,
    droppingCourseFromRegLoading: dropCourseFromRegInput.loading,
    updatingCourseRegisteredFields: updateCourseRegisteredFieldsMutation.loading,
    updatingRegistrationFields: updateRegistrationFieldsMutation.loading,
    updatingRegistrationScores: updateRegistrationScoresMutation.loading,
    submittingCourseRegistration: adminSubmitCourseRegMutation.loading,
    senateApproving: senateApproveMutation.loading,
    updatingFaculty: updateFacultyMutation.loading,
    deletingFaculty: deleteFacultyMutation.loading,
    updatingDepartment: updateDepartmentMutation.loading,
    deletingDepartment: deleteDepartmentMutation.loading,
    updatingProgramme: updateProgrammeMutation.loading,
    deletingProgramme: deleteProgrammeMutation.loading,
    creatingProgrammeModule: createProgrammeModuleMutation.loading,
    updatingProgrammeModule: updateProgrammeModuleMutation.loading,
    deletingProgrammeModule: deleteProgrammeModuleMutation.loading,
    togglingProgrammeModule: toggleProgrammeModuleMutation.loading,
  };
}
